import { useEffect, useState } from "react";
import { useBattle } from "./BattleProvider";
import { ArrowRight, Camera, Check, ShieldCheck, X, Sparkles } from "lucide-react";
import { spriteUrl } from "@/lib/pokemon";
import { useToast } from "@/hooks/use-toast";

export function TradeExecutionFlow() {
  const { pendingTrade, advanceTradeStep, setMiaConfirmed, closeTrade, completeTrade } = useBattle();
  const { toast } = useToast();
  const [snapStep, setSnapStep] = useState<1 | 2>(1);

  // Mia auto-confirms after 2s when both sides need to confirm.
  useEffect(() => {
    if (!pendingTrade) return;
    if (pendingTrade.step === "both-confirm" && !pendingTrade.miaConfirmed) {
      const t = setTimeout(() => setMiaConfirmed(true), 2000);
      return () => clearTimeout(t);
    }
  }, [pendingTrade, setMiaConfirmed]);

  if (!pendingTrade) return null;
  const t = pendingTrade;

  return (
    <div
      role="dialog"
      aria-label="Action this trade"
      aria-modal="true"
      className="fixed inset-0 z-[90] flex items-end md:items-center justify-center px-3 py-3 bg-black/65 backdrop-blur-sm anim-fade-up"
      onClick={closeTrade}
    >
      <div
        className="relative w-full max-w-md v2-neon-card rounded-3xl overflow-hidden anim-fade-scale"
        style={{ background: "var(--surface-dark-7)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-trade-execution"
      >
        <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
          <div className="v2-aurora v2-aurora--green" style={{ width: "26vmax", height: "26vmax", top: "-12vmax", right: "-10vmax", opacity: 0.3 }} />
        </div>
        <header className="relative px-5 pt-5 pb-3 flex items-center justify-between">
          <div>
            <span className="v2-eyebrow"><span className="v2-eyebrow__dot" />TRADE</span>
            <h3 className="v2-display text-base tracking-tight mt-2" style={{ color: "var(--text-ink-bright)" }}>
              Step {stepNumber(t.step)} of 4
            </h3>
          </div>
          <button
            onClick={closeTrade}
            data-testid="button-close-trade-execution"
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-ink-bright-faint)" }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Step indicator */}
        <div className="relative px-5 pb-4 flex items-center gap-1.5">
          {(["confirm", "both-confirm", "upload", "complete"] as const).map((s, i) => {
            const active = stepNumber(t.step) > i;
            const current = t.step === s;
            return (
              <div
                key={s}
                className="h-1.5 rounded-full flex-1 transition-all"
                style={{
                  background: current
                    ? "linear-gradient(90deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)"
                    : active
                      ? "var(--accent-fill)"
                      : "hsl(230 25% 12%)",
                  boxShadow: current ? "0 0 8px var(--accent-fill-glow-strong)" : undefined,
                }}
              />
            );
          })}
        </div>

        <div className="relative px-5 pb-6">
          {t.step === "confirm" && (
            <div data-testid="trade-step-confirm">
              <p className="text-[14px] font-semibold leading-relaxed" style={{ color: "var(--text-ink-bright-soft)" }}>
                Once you and {t.friendName} complete this trade in Pokémon GO, tap Confirm and we'll update both your inventories.
              </p>
              <SwapPreview t={t} />
              <div className="mt-5 flex gap-2">
                <button
                  data-testid="button-trade-cancel"
                  className="flex-1 rounded-full font-semibold py-2.5 text-sm"
                  style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-ink-bright-faint)" }}
                  onClick={closeTrade}
                >
                  Cancel
                </button>
                <button
                  data-testid="button-trade-im-trading"
                  className="v2-neon-cta flex-[2] rounded-full py-2.5 inline-flex items-center justify-center gap-1.5"
                  onClick={() => advanceTradeStep("both-confirm")}
                >
                  I'm trading now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {t.step === "both-confirm" && (
            <div data-testid="trade-step-both-confirm">
              <p className="text-[13px] text-muted-foreground leading-relaxed">
                Both sides confirm when the trade animation completes in Pokémon GO.
              </p>
              <SwapPreview t={t} />
              <div className="mt-5 grid grid-cols-2 gap-2">
                <ConfirmCard
                  label="You"
                  done={false}
                  testid="confirm-you"
                  onConfirm={() => advanceTradeStep("upload")}
                  enabled={t.miaConfirmed}
                  hint={t.miaConfirmed ? "Mia confirmed, your turn" : "Waiting for Mia..."}
                />
                <ConfirmCard
                  label={t.friendName}
                  done={t.miaConfirmed}
                  testid="confirm-friend"
                  hint={t.miaConfirmed ? "Confirmed" : "Confirming..."}
                />
              </div>
            </div>
          )}

          {t.step === "upload" && (
            <div data-testid="trade-step-upload">
              <p className="text-[14px] font-semibold leading-relaxed">
                Snap your new {t.theirs.species} so we can update your inventory.
              </p>
              <p className="text-[12px] text-muted-foreground mt-1">
                Two screenshots, summary + appraisal. Pre-filled to {t.theirs.species}.
              </p>

              <div
                className="mt-4 rounded-2xl p-5 text-center"
                style={{
                  background: "var(--surface-dark-9)",
                  border: "2px dashed var(--accent-fill-glow)",
                  boxShadow: "0 0 12px var(--accent-fill-glow-faint)",
                }}
              >
                <Camera className="w-7 h-7 mx-auto" style={{ color: "var(--accent-fill)" }} />
                <p className="text-sm font-semibold mt-2" style={{ color: "var(--text-ink-bright)" }}>
                  Snap {snapStep} of 2: {snapStep === 1 ? "Summary screen" : "Appraisal stats"}
                </p>
                <p className="text-[11px] mt-1" style={{ color: "hsl(142 18% 72%)" }}>
                  Pre-filled species: {t.theirs.species}
                </p>
                <button
                  data-testid={`button-trade-snap-${snapStep}`}
                  className="v2-neon-cta mt-4 rounded-full px-4 py-2 text-xs"
                  onClick={() => {
                    if (snapStep === 1) setSnapStep(2);
                    else {
                      advanceTradeStep("complete");
                      completeTrade();
                    }
                  }}
                >
                  Use sample snap {snapStep}
                </button>
              </div>

              <p className="text-[11px] text-muted-foreground mt-3 text-center">
                The shell of {t.theirs.species} is already in your inventory; this snap fills the deep details.
              </p>
            </div>
          )}

          {t.step === "complete" && (
            <div data-testid="trade-step-complete">
              <div className="flex flex-col items-center text-center py-2">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
                    boxShadow: "0 0 28px var(--accent-fill-glow)",
                  }}
                >
                  <Check className="w-8 h-8" style={{ color: "var(--text-on-accent)" }} strokeWidth={3.5} />
                </div>
                <h4 className="v2-display mt-3 text-lg tracking-tight" style={{ color: "var(--text-ink-bright)" }}>Trade complete</h4>
                <p className="text-[13px] mt-1.5 leading-relaxed max-w-xs" style={{ color: "hsl(142 18% 78%)" }}>
                  Your inventory is updated and {t.friendName} has been notified.
                </p>
                <p
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold"
                  style={{
                    background: "var(--accent-fill-glow-faint)",
                    color: "var(--accent-fill)",
                    border: "1px solid var(--accent-fill-glow)",
                  }}
                >
                  <Sparkles className="w-3 h-3" />
                  Don't forget to top up the deep details on your shallow entry
                </p>
              </div>

              <SwapPreview t={t} flipped />

              <button
                data-testid="button-trade-done"
                className="v2-neon-cta w-full mt-5 rounded-full py-3"
                onClick={() => {
                  closeTrade();
                  toast({
                    title: "Inventory updated",
                    description: `${t.theirs.species} is now in your box. Top up the deep details when you can.`,
                  });
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function stepNumber(s: "confirm" | "both-confirm" | "upload" | "complete"): number {
  return s === "confirm" ? 1 : s === "both-confirm" ? 2 : s === "upload" ? 3 : 4;
}

function SwapPreview({ t, flipped = false }: { t: NonNullable<ReturnType<typeof useBattle>["pendingTrade"]>; flipped?: boolean }) {
  return (
    <div className="mt-4 v2-neon-card-soft p-4">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <SpriteCard label={flipped ? `${t.friendName} gives` : "You give"} dex={t.yours.dexNumber} species={t.yours.species} cp={t.yours.cp} dim={!flipped} />
        <ArrowRight className="w-5 h-5" style={{ color: "var(--accent-fill)" }} />
        <SpriteCard label={flipped ? "You receive" : `${t.friendName} gives`} dex={t.theirs.dexNumber} species={t.theirs.species} cp={t.theirs.cp} dim={false} />
      </div>
    </div>
  );
}

function SpriteCard({ label, dex, species, cp, dim }: { label: string; dex: number; species: string; cp: number; dim: boolean }) {
  return (
    <div className="text-center">
      <p className="v2-display-up text-[9px]" style={{ color: "var(--accent-fill)" }}>{label}</p>
      <div className="relative mt-1 mx-auto" style={{ width: 76, height: 84, opacity: dim ? 0.55 : 1, filter: dim ? "none" : "drop-shadow(0 0 8px var(--accent-fill-glow))" }}>
        <div
          aria-hidden
          className="v2-hex-frame absolute inset-0"
          style={{ background: "linear-gradient(180deg, var(--accent-fill), var(--accent-fill))" }}
        />
        <div
          aria-hidden
          className="v2-hex-frame absolute"
          style={{ inset: 2, background: "var(--surface-dark-7)" }}
        />
        <div className="relative w-full h-full flex items-center justify-center">
          <img src={spriteUrl(dex)} alt={species} className="w-14 h-14 object-contain" style={{ imageRendering: "pixelated" }} />
        </div>
      </div>
      <p className="text-[12px] font-bold mt-1 truncate" style={{ color: "var(--text-ink-bright)" }}>{species}</p>
      <p className="text-[10px] font-mono tabular-nums" style={{ color: "hsl(142 18% 72%)" }}>CP {cp}</p>
    </div>
  );
}

function ConfirmCard({
  label, done, testid, onConfirm, enabled, hint,
}: { label: string; done: boolean; testid: string; onConfirm?: () => void; enabled?: boolean; hint?: string }) {
  return (
    <div
      className="rounded-2xl p-4 text-center"
      style={{
        background: done ? "var(--accent-fill-glow-faint)" : "var(--surface-dark-9)",
        border: `1.5px solid ${done ? "var(--accent-fill)" : "var(--accent-fill-glow)"}`,
        boxShadow: done ? "0 0 14px var(--accent-fill-glow-subtle)" : undefined,
      }}
      data-testid={testid}
    >
      <p className="v2-display-up text-[10px]" style={{ color: "hsl(142 70% 90%)" }}>{label}</p>
      <div
        className="mt-2 mx-auto w-10 h-10 rounded-full flex items-center justify-center"
        style={{
          background: done ? "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)" : "var(--surface-dark-12)",
          color: done ? "var(--text-on-accent)" : "var(--text-ink-faint)",
        }}
      >
        {done ? <Check className="w-5 h-5" strokeWidth={3} /> : <ShieldCheck className="w-5 h-5" />}
      </div>
      {onConfirm && !done && (
        <button
          onClick={onConfirm}
          disabled={!enabled}
          data-testid={`${testid}-button`}
          className={`mt-3 w-full rounded-full py-2 text-[12px] font-bold ${enabled ? "v2-neon-cta" : ""}`}
          style={enabled ? undefined : { background: "var(--surface-dark-12)", color: "hsl(142 18% 60%)", border: "1px solid hsl(142 30% 22%)" }}
        >
          {enabled ? "Confirm trade complete" : "Waiting"}
        </button>
      )}
      {hint && <p className="text-[10px] mt-2" style={{ color: "hsl(142 18% 72%)" }}>{hint}</p>}
    </div>
  );
}
